/**
 * Destination Images tab: keywords + structured image rows for quality review.
 */
import React, { useCallback, useState } from "react";
import {
  EntityCustomViewParams,
  PropertyFieldBinding,
  useAuthController,
  useSnackbarController,
  type Property,
} from "@firecms/core";
import { Button, Typography } from "@firecms/ui";
import { getApps } from "firebase/app";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { CONTENT_LOCALES, LOCALE_LABELS } from "../constants";
import { DestinationImageItemField } from "./DestinationImageItemField";
import {
  isImageDetailKey,
  isImageSummaryKey,
} from "../lib/destinationImageFields";

const CONTENT_ENGINE_URL = import.meta.env.VITE_CONTENT_ENGINE_URL?.replace(/\/$/, "") ?? "";

const localeStringMap = Object.fromEntries(
  CONTENT_LOCALES.map((locale) => [
    locale,
    { name: LOCALE_LABELS[locale], dataType: "string" as const },
  ]),
);

/** Ordered image-row fields: ID and preview first, caption next, sources, then raw metadata. */
export const destinationImageItemProperties = {
  photoId: {
    name: "Image ID",
    dataType: "string" as const,
    description:
      "Stable photo ID (Unsplash id or commons:pageId:…). Use this when referring to a specific gallery image.",
  },
  imageUrl: {
    name: "Image",
    dataType: "string" as const,
    url: "image" as const,
    description: "Delivery URL (Unsplash or Wikimedia Commons thumbnail).",
  },
  caption_localized: {
    name: "Caption (hero pill)",
    dataType: "map" as const,
    minimalistView: true,
    description:
      "Short place name on the hero. Prefer Wikidata per locale; English uses Vision or a specific location name. Empty locales are filled from English with destination/country token localization. Search keywords and destination-only names are never used — empty pill preferred over wrong.",
    properties: localeStringMap,
  },
  caption_source: {
    name: "Caption source",
    dataType: "map" as const,
    minimalistView: true,
    description:
      "Where each locale caption came from (Wikidata, Unsplash/Commons location, Vision landmark, English fallback, or destination name).",
    properties: localeStringMap,
  },
  caption_source_kind: {
    name: "Caption source kind",
    dataType: "map" as const,
    minimalistView: true,
    description:
      "Machine-readable caption source (wikidata, vision, unsplash, english_fallback, …). Weak kinds hide the hero pill.",
    properties: localeStringMap,
  },
  landmark_detection: {
    name: "Landmark detection",
    dataType: "string" as const,
    description:
      "Whether Google Vision LANDMARK_DETECTION ran. Forced for atmosphere slots and when location is city-only.",
    enumValues: {
      pending: "Pending (processing)",
      skipped: "Skipped (already specific place)",
      ran_no_result: "Ran — no result",
      ran_with_result: "Ran — result found",
    },
  },
  keyword: {
    name: "Search keyword",
    dataType: "string" as const,
    description: "Landmark or atmosphere query that won this slot (not shown as the pill).",
  },
  gallery_role: {
    name: "Gallery role",
    dataType: "string" as const,
    description: "landmark (named place coverage) or atmosphere (whole-place feel).",
    enumValues: {
      landmark: "Landmark",
      atmosphere: "Atmosphere",
    },
  },
  location: {
    name: "Location",
    dataType: "map" as const,
    minimalistView: true,
    properties: {
      name: { name: "Name", dataType: "string" as const },
      city: { name: "City", dataType: "string" as const },
      country: { name: "Country", dataType: "string" as const },
      latitude: { name: "Latitude", dataType: "number" as const },
      longitude: { name: "Longitude", dataType: "number" as const },
    },
  },
  location_source: {
    name: "Location source",
    dataType: "string" as const,
    description: "Unsplash photo.location or Wikidata coords for Commons images.",
  },
  photographer: {
    name: "Photographer",
    dataType: "map" as const,
    minimalistView: true,
    properties: {
      name: { name: "Name", dataType: "string" as const },
      username: { name: "Username", dataType: "string" as const },
      url: { name: "Profile", dataType: "string" as const, url: true },
    },
  },
  photoUrl: {
    name: "Source page",
    dataType: "string" as const,
    url: true,
    description: "Unsplash photo page or Wikimedia Commons file page.",
  },
  wikiTitle: {
    name: "Commons title",
    dataType: "string" as const,
    description: "Wikimedia Commons File: title when provider is wikimedia_commons.",
  },
  license: {
    name: "License",
    dataType: "string" as const,
    description: "Short license name for Commons images (e.g. CC BY-SA 4.0).",
  },
  licenseUrl: {
    name: "License URL",
    dataType: "string" as const,
    url: true,
  },
  attribution: {
    name: "Attribution",
    dataType: "string" as const,
  },
  creditLine: {
    name: "Credit line",
    dataType: "string" as const,
    description: "Hero credit text (includes license for Commons).",
  },
  wikidata_id: {
    name: "Wikidata ID",
    dataType: "string" as const,
  },
  wikidata_url: {
    name: "Wikidata",
    dataType: "string" as const,
    url: true,
    description: "Entity page used for localized captions.",
  },
  wikidata_source: {
    name: "Wikidata source",
    dataType: "string" as const,
  },
  landmark: {
    name: "Landmark (Vision)",
    dataType: "map" as const,
    minimalistView: true,
    properties: {
      description: { name: "Name", dataType: "string" as const },
      score: { name: "Score", dataType: "number" as const },
      mid: { name: "Freebase mid", dataType: "string" as const },
      latitude: { name: "Latitude", dataType: "number" as const },
      longitude: { name: "Longitude", dataType: "number" as const },
    },
  },
  landmark_source: {
    name: "Landmark source",
    dataType: "string" as const,
    description: "Set when Landmark detection = Ran — result found.",
  },
  focalX: { name: "Focal X", dataType: "string" as const },
  focalY: { name: "Focal Y", dataType: "string" as const },
  imageWidth: { name: "Width", dataType: "number" as const },
  imageHeight: { name: "Height", dataType: "number" as const },
  orientation: { name: "Orientation", dataType: "string" as const },
  provider: {
    name: "Provider",
    dataType: "string" as const,
    description: "unsplash or wikimedia_commons.",
  },
  description: {
    name: "Photographer description",
    dataType: "string" as const,
    multiline: true,
    description: "Photographer text. Not used on the hero pill.",
  },
} as const;

const unclassifiedImageFields = Object.keys(destinationImageItemProperties).filter(
  (key) => !isImageSummaryKey(key) && !isImageDetailKey(key),
);
if (unclassifiedImageFields.length > 0) {
  throw new Error(`Unclassified destination image fields: ${unclassifiedImageFields.join(", ")}`);
}

export const destinationKeywordsProperty = {
  name: "Keywords",
  dataType: "array" as const,
  of: { dataType: "string" as const },
  description:
    "Exactly 10 search strings: first 8 landmarks (landmark + city/region + country), last 2 geo-anchored mood/atmosphere (e.g. evening street City Country). The image service prefers Wikimedia Commons for nearby landmarks, uses mood keywords for atmosphere (or injects place-atmosphere queries if mood is missing), and may return up to 12 gallery images. Prefer candid, natural-light imagery over staged stock style.",
};

export const destinationImagesProperty = {
  name: "Images",
  dataType: "array" as const,
  of: {
    name: "Image",
    dataType: "map" as const,
    Field: DestinationImageItemField,
    expanded: false,
    properties: destinationImageItemProperties,
  },
  description:
    "Hero images (up to 12; flexible count). Landmark slots prefer Commons when Wikidata has a nearby image; mood/atmosphere stays Unsplash. Empty Unsplash location and far Commons coords are rejected. Each row shows the photo, ID, captions, role, place, and detection status. Use Re-fetch images to rebuild the gallery from keywords after image-service changes.",
};

async function reloadImagesIntoForm(
  entityId: string,
  setFieldValue: (key: string, value: unknown) => void,
): Promise<number | null> {
  const app = getApps()[0];
  if (!app) return null;
  const snap = await getDoc(doc(getFirestore(app), "destinations", entityId));
  if (!snap.exists()) return null;
  const data = snap.data() as { images?: unknown };
  const images = Array.isArray(data.images) ? data.images : [];
  setFieldValue("images", images);
  return images.length;
}

function DestinationImagesActions({
  entityId,
  setFieldValue,
}: {
  entityId?: string;
  setFieldValue: (key: string, value: unknown) => void;
}) {
  const snackbar = useSnackbarController();
  const authController = useAuthController();
  const [busy, setBusy] = useState<"replace" | "append" | null>(null);

  const runReFetch = useCallback(
    async (mode: "replace" | "append") => {
      if (!entityId) {
        snackbar.open({
          type: "error",
          message: "Save the destination before fetching images.",
        });
        return;
      }
      if (!CONTENT_ENGINE_URL) {
        snackbar.open({
          type: "error",
          message: "VITE_CONTENT_ENGINE_URL is not configured.",
        });
        return;
      }
      const token = await authController.getAuthToken();
      if (!token?.trim()) {
        snackbar.open({
          type: "error",
          message: "Not authenticated. Please sign in again.",
        });
        return;
      }
      setBusy(mode);
      try {
        const res = await fetch(`${CONTENT_ENGINE_URL}/re-fetch-images`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ document_id: entityId, mode }),
        });
        const data = (await res.json()) as {
          ok?: boolean;
          message?: string;
          error?: string;
        };
        if (!res.ok || !data.ok) {
          snackbar.open({
            type: "error",
            message: data.error ?? "Image fetch failed",
          });
          return;
        }
        const count = await reloadImagesIntoForm(entityId, setFieldValue);
        snackbar.open({
          type: "success",
          message:
            count != null
              ? `${data.message ?? "Images updated"} (${count} in form)`
              : (data.message ?? "Images updated. Reload if the list looks stale."),
        });
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Request failed",
        });
      } finally {
        setBusy(null);
      }
    },
    [authController, entityId, setFieldValue, snackbar],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="small"
          variant="filled"
          color="primary"
          disabled={!!busy}
          onClick={() => void runReFetch("replace")}
        >
          {busy === "replace" ? "Re-fetching…" : "Re-fetch images"}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!!busy}
          onClick={() => void runReFetch("append")}
        >
          {busy === "append" ? "Fetching…" : "Fetch additional"}
        </Button>
      </div>
      <Typography variant="caption" className="text-gray-600">
        Re-fetch replaces the gallery from current keywords (Commons + Unsplash).
        Fetch additional keeps existing photos and appends new ones.
      </Typography>
    </div>
  );
}

export function createDestinationsImagesView() {
  const Builder = function (props: EntityCustomViewParams<Record<string, unknown>>) {
    const { formContext, entity } = props;
    if (!formContext) return null;
    const entityId = entity?.id ?? formContext.entityId;
    return (
      <div className="flex flex-col gap-6 p-4 min-w-0">
        <div className="flex flex-col gap-3">
          <Typography variant="label" className="font-medium">
            Keywords and images
          </Typography>
          <DestinationImagesActions
            entityId={entityId}
            setFieldValue={formContext.setFieldValue}
          />
        </div>
        <PropertyFieldBinding
          propertyKey="keywords"
          property={destinationKeywordsProperty as Property}
          context={formContext}
        />
        <PropertyFieldBinding
          propertyKey="images"
          property={destinationImagesProperty as Property}
          context={formContext}
        />
      </div>
    );
  };
  Object.defineProperty(Builder, "name", {
    value: "DestinationsImagesBuilder",
    configurable: true,
  });
  return {
    key: "images",
    name: "Images",
    includeActions: true as const,
    position: "end" as const,
    Builder,
  };
}
